'use client'

import {useContext, useEffect} from 'react'
import Link from 'next/link'
import {ActivityCover} from '@fitro/customer-ui/ActivityCover'
import {ActivityTerm} from '@fitro/customer-ui/ActivityTerm'
import {UsersRound} from 'lucide-react'
import {DetailsFact, DetailsFactIcon} from '../shared/DetailsFact'
import {PublicBookingRules} from './PublicBookingRules'
import {ActivityDescription} from '../shared/ActivityDescription'
import {PublicEntryChromeContext} from './PublicEntryChromeContext'
import {useTranslation} from '../../../hooks/useTranslation'
import {useAuth} from '@fitro/customer-host/auth'
import {getClassCoverGradient} from '../../../lib/classColors'
import {formatEventPaymentMethods} from '@fitro/event-contract'
import {PublicOfferBackLink} from './PublicOfferBackLink'
import type {PublicScheduleEvent} from '../../../services/public-schedule.service'
import {buildAuthHref} from './publicEntryLinks'
import {getAuthenticatedEventDetailsHref, getPublicEventBookingMode, openTermFromStudioPage} from './publicEventLinks'
import {resolvePublicAssetUrl} from './publicAssetUrl'
import {PublicGuestBooking} from './PublicGuestBooking'
import {getButtonClasses} from '@fitro/customer-ui/Button'
import {PublicTermDetails, usePublicTermPrice, usePublicTermSession} from './PublicTermDetails'

type PublicEventDetailsProps = {
    event: PublicScheduleEvent
    partnerSlug?: string
}

const PRIMARY_CTA_CLASS_NAME = getButtonClasses({variant: 'primary', className: 'w-full gap-2'})
const SECONDARY_CTA_CLASS_NAME = getButtonClasses({variant: 'secondary', className: 'w-full'})

function PublicEventBookingCta({event, partnerSlug}: PublicEventDetailsProps) {
    const {t} = useTranslation('landing')
    const {isLoaded, isSignedIn} = useAuth()
    const eventHref = getAuthenticatedEventDetailsHref(event)
    const signedIn = isLoaded && isSignedIn
    const signInHref = buildAuthHref('/sign-in', eventHref)
    const bookingMode = getPublicEventBookingMode(event, signedIn)

    if (bookingMode === 'waitlist') {
        return (
            <div className="space-y-2">
                <p className="text-[14px] text-[var(--landing-neutral-600)]">{t('publicEntry.guestCheckout.fullWaitlist')}</p>
                <Link href={signInHref} className={SECONDARY_CTA_CLASS_NAME}>{t('publicEntry.guestCheckout.signIn')}</Link>
            </div>
        )
    }
    if (!bookingMode) return null
    if (bookingMode !== 'guest' || !partnerSlug) {
        // Signed in, one full load opens the Term in the app, a class over its calendar on a wide screen.
        return signedIn
            ? <a href={eventHref} className={PRIMARY_CTA_CLASS_NAME} onClick={(click) => openTermFromStudioPage(click, event)}>{t('publicEntry.cta.reserve')}</a>
            : <Link href={signInHref} className={PRIMARY_CTA_CLASS_NAME}>{t('publicEntry.cta.reserve')}</Link>
    }
    return (
        <PublicGuestBooking
            className={PRIMARY_CTA_CLASS_NAME}
            event={event}
            partnerSlug={partnerSlug}
            signInHref={signInHref}
        >
            {t('publicEntry.cta.reserve')}
        </PublicGuestBooking>
    )
}

// Once a one-off event's guest form is open the visitor has chosen their price: the details keep only what they book.
const HIDDEN_IN_GUEST_FORM = 'group-has-[[data-guest-checkout-panel]]/event:hidden'

function PublicEventDetailsContent({event, partnerSlug}: PublicEventDetailsProps) {
    const {t} = useTranslation('landing')
    const title = event.className?.trim() || t('publicEntry.events.fallbackTitle')
    const description = event.description?.trim()
    const fullDescriptionHtml = event.fullDescriptionHtml?.trim()
    const {session} = usePublicTermSession(event)
    const imageUrl = resolvePublicAssetUrl(event.classCoverImageUrl)
    const {price, priceNode, lowestPriceNote, firstEntryNote, benefitCards} = usePublicTermPrice(event)
    const paymentMethods = formatEventPaymentMethods(event.acceptedPaymentMethods, {
        online: t('publicEntry.events.paymentMethod.online'),
        cash: t('publicEntry.events.paymentMethod.cash'),
    }, event.priceMinor)
    const availabilityKey = event.availabilityStatus === 'last_spots'
        ? 'lastSpots'
        : event.availabilityStatus === 'registration_closed'
            ? 'registrationClosed'
            : event.availabilityStatus


    // A recurring class has little to read: show a short header and go straight to booking.
    if (event.isCyclical !== false) {
        return (
            <article data-testid="public-event-compact" className="mx-auto max-w-xl space-y-5">
                <header className="rounded-[12px] border border-[var(--landing-neutral-100)] bg-white p-5 sm:p-6">
                    <h1 id="public-event-details-title" className="break-words text-xl font-semibold leading-7 text-gray-900 dark:text-gray-100">{title}</h1>
                    <div data-testid="public-event-session" className="mt-1"><ActivityTerm {...session} timeFirst align="start"/></div>
                    <PublicTermDetails event={event} layout="column" className="mt-4"/>
                </header>
                <div className="w-full empty:hidden">
                    <PublicEventBookingCta event={event} partnerSlug={partnerSlug} />
                </div>
            </article>
        )
    }

    return (
        <div className="space-y-5">
            <article
                data-testid="public-event-details-card"
                className="group/event grid items-start gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(360px,2fr)] xl:gap-8"
            >
                <div className="order-2 flex min-w-0 flex-col xl:py-3">
                    <div className="min-w-0">
                        <h1 id="public-event-details-title" className="break-words text-xl font-semibold leading-7 text-gray-900 dark:text-gray-100">{title}</h1>
                        <div data-testid="public-event-session" className="mt-1"><ActivityTerm {...session} timeFirst align="start"/></div>
                    </div>
                    {/* Once the guest form is open the visitor has chosen their price: the facts keep only what they book. */}
                    <PublicTermDetails event={event} layout="column" className="mt-4" priceClassName={HIDDEN_IN_GUEST_FORM} price={price ? <>
                        <span>{priceNode}</span>
                        {lowestPriceNote}
                        {firstEntryNote}
                        {benefitCards}
                        {paymentMethods ? <p className="mt-0.5 text-xs font-normal leading-4 text-gray-500">{t('publicEntry.events.paymentMethods', {methods: paymentMethods})}</p> : null}
                    </> : null}>
                        <DetailsFact label={t('publicEntry.details.availability')} icon={<DetailsFactIcon muted={false}><UsersRound className="h-4 w-4"/></DetailsFactIcon>}>
                            <span data-testid="public-event-availability-summary">{t(`publicEntry.schedule.availability.${availabilityKey}`)}</span>
                        </DetailsFact>
                    </PublicTermDetails>

                    <div className="mt-5 w-full empty:hidden">
                        <PublicEventBookingCta event={event} partnerSlug={partnerSlug} />
                    </div>
                </div>

                <div
                    data-testid="public-event-cover"
                    className="order-1 w-full self-stretch overflow-hidden rounded-[14px] border border-[var(--landing-neutral-100)] shadow-[0_12px_28px_rgba(57,98,198,0.07)]"
                    style={{background: getClassCoverGradient(null, event.id || title)}}
                >
                    <ActivityCover priority src={imageUrl} background={getClassCoverGradient(null, event.id || title)}/>
                </div>
            </article>

            <ActivityDescription key={`description:${event.id}`} description={description} fullDescriptionHtml={fullDescriptionHtml}/>
            <PublicBookingRules key={`rules:${event.id}`} event={event}/>
        </div>
    )
}

export function PublicEventDetailsPage({backHref, event, partnerSlug}: PublicEventDetailsProps & {backHref: string}) {
    const recurring = event.isCyclical !== false
    const fromPricing = backHref === '/cennik'
    const toSchedule = !fromPricing && (recurring || backHref === '/')
    const {t} = useTranslation('landing')
    const chrome = useContext(PublicEntryChromeContext)
    useEffect(() => chrome?.registerOfferPage(recurring ? 'schedule' : 'events'), [chrome, recurring])
    return (
        <section data-public-entry-details-route className="pb-3 pt-0 sm:pb-5" aria-labelledby="public-event-details-title">
            <PublicOfferBackLink href={toSchedule ? '/' : backHref}>
                {t(fromPricing ? 'publicEntry.details.backToPricing' : toSchedule ? 'publicEntry.details.backToSchedule' : 'publicEntry.details.backToEvents')}
            </PublicOfferBackLink>
            <PublicEventDetailsContent event={event} partnerSlug={partnerSlug} />
        </section>
    )
}
